// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionScheduling.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import EventsUtils from 'EventUtils';
import actions from '../Izenda.PermissionActions';
import connect from 'share/enhancers/Izenda.Connect';
import { get, isNil } from 'lodash/fp';

import Input from 'share/components/controls/Izenda.Input';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import { SECTION } from 'logic/data/Izenda.PermissionConstants';
import UserRolePermission from './Izenda.UserRolePermission';
import CheckboxAllPermission from './Izenda.CheckboxAllPermission';
const UNCHECK_VALUE = 0;

@connect({ toClass: true }, actions)
export default class PermissionScheduling extends Component {
  static propTypes = {
    permissionStore: PropTypes.object.isRequired,
    permission: PropTypes.object.isRequired
  };

  onSearchSchedule = (searchKey: string) => {
    const { permissionStore } = this.props;
    permissionStore.loadPartialPermissionSchedule(null, true, searchKey);
  };

  updateSelectedNode = (node: any, parent: any, accessModel: any) => {
    const { permissionStore } = this.props;
    permissionStore.updateSchedulingLimitsData(node, parent, accessModel);
  };

  loadSchedulingLimits = (pageInfo: any) => {
    const { permissionStore } = this.props;
    permissionStore.loadPartialPermissionSchedule(pageInfo);
  };

  render() {
    const notifyChange = EventsUtils.notifyChange;
    const { permissionStore, permission, localization, isTemplatePermissions } = this.props;
    const { tenantDetail } = permissionStore;
    const { scheduling } = permission;
    const tenantPermission = get('scheduling', tenantDetail);

    return (
      !permission.fullReportAndDashboardAccess &&
      (isNil(tenantDetail) || tenantDetail.scheduling.tenantAccess !== UNCHECK_VALUE) && (
        <div className={SECTION.Scheduling}>
          <GridLayout className='izenda-GridLayout-Custom'>
            <GridLayout.Row className='u_textBold u_paddingTopBottom izenda-PermissionCategory-Header u_noMarginAll'>
              <GridLayout.Col cols={2}>
                <a
                  className={this.props.getIconClass('isShowScheduling')}
                  onClick={this.props.toggle.bind(this, 'isShowScheduling')}
                />
                {localization.getText('COMMON_LABEL_SCHEDULING')}
              </GridLayout.Col>
              <GridLayout.Col cols={9}>
                <CheckboxAllPermission
                  permission={permission}
                  readOnly={this.props.readOnly}
                  disabled={isTemplatePermissions}
                  section={scheduling}
                  tenantPermission={tenantPermission}
                  tenantDetail={tenantDetail}
                />
              </GridLayout.Col>
              {permission.isTenantSetup && (
                <GridLayout.Col cols={1} className='u_textCenter'>
                  <Input
                    type='checkbox'
                    readOnly={this.props.readOnly}
                    disabled={isTemplatePermissions}
                    indeterminate={this.props.getIndeterminateCommon(scheduling)}
                    checked={this.props.getCheckedCommon(scheduling.tenantAccess)}
                    onChange={notifyChange(
                      permission.updateTenantAccessParent.bind(permission, scheduling),
                      EventsUtils.CHECKBOX
                    )}
                  />
                </GridLayout.Col>
              )}
            </GridLayout.Row>
          </GridLayout>
          {permissionStore.isShowScheduling && (
            <GridLayout>
              {(isNil(tenantDetail) || tenantDetail.scheduling.schedulingLimits.tenantAccess !== UNCHECK_VALUE) && (
                <GridLayout.Row className='u_paddingTop u_paddingBottom u_noMarginAll u_borderBottom'>
                  <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
                    {localization.getText('PERMISSION_LABEL_SCHEDULING_LIMITS')}
                  </GridLayout.Col>
                  <GridLayout.Col cols={9}>
                    <div className='u_inlineBlock u_fullWidth'>
                      <UserRolePermission
                        {...this.props}
                        getData={this.loadSchedulingLimits}
                        searchRoles={this.onSearchSchedule}
                        rolePermission={permissionStore.roleSchedulingLimits}
                        updateSelectedNode={this.updateSelectedNode}
                      />
                    </div>
                  </GridLayout.Col>
                  {permission.isTenantSetup && (
                    <GridLayout.Col cols={1} className='u_textCenter'>
                      <Input
                        type='checkbox'
                        readOnly={this.props.readOnly}
                        disabled={isTemplatePermissions}
                        checked={this.props.getCheckedCommon(scheduling.schedulingLimits.tenantAccess)}
                        onChange={notifyChange(
                          permission.updateTenantAccess.bind(permission, scheduling, scheduling.schedulingLimits),
                          EventsUtils.CHECKBOX
                        )}
                      />
                    </GridLayout.Col>
                  )}
                </GridLayout.Row>
              )}
              {(isNil(tenantDetail) || this.props.checkShowGroupCommon(tenantDetail.scheduling.schedulingScope)) && (
                <GridLayout.Row className='u_paddingTop u_paddingBottom u_noMarginAll'>
                  <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
                    {localization.getText('PERMISSION_LABEL_SCHEDULING_SCOPE')}
                  </GridLayout.Col>
                  <GridLayout.Col cols={9}>
                    {(isNil(tenantDetail) || tenantDetail.scheduling.schedulingScope.systemUsers) && (
                      <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                        <label>
                          <Input
                            type='checkbox'
                            readOnly={this.props.readOnly}
                            disabled={isTemplatePermissions}
                            checked={this.props.getCheckedCommon(scheduling.schedulingScope.systemUsers)}
                            onChange={notifyChange(
                              permission.updateData.bind(permission, scheduling.schedulingScope, 'systemUsers'),
                              EventsUtils.CHECKBOX
                            )}
                          />
                          <span>{localization.getText('PERMISSION_LABEL_SYSTEM_USERS')}</span>
                        </label>
                      </div>
                    )}
                    {(isNil(tenantDetail) || tenantDetail.scheduling.schedulingScope.externalUsers) && (
                      <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                        <label>
                          <Input
                            type='checkbox'
                            readOnly={this.props.readOnly}
                            disabled={isTemplatePermissions}
                            checked={this.props.getCheckedCommon(scheduling.schedulingScope.externalUsers)}
                            onChange={notifyChange(
                              permission.updateData.bind(permission, scheduling.schedulingScope, 'externalUsers'),
                              EventsUtils.CHECKBOX
                            )}
                          />
                          <span>{localization.getText('PERMISSION_LABEL_EXTERNAL_USERS')}</span>
                        </label>
                      </div>
                    )}
                  </GridLayout.Col>
                  {permission.isTenantSetup && (
                    <GridLayout.Col cols={1} className='u_textCenter'>
                      <Input
                        type='checkbox'
                        readOnly={this.props.readOnly}
                        disabled={isTemplatePermissions}
                        checked={this.props.getCheckedCommon(scheduling.schedulingScope.tenantAccess)}
                        onChange={notifyChange(
                          permission.updateTenantAccess.bind(permission, scheduling, scheduling.schedulingScope),
                          EventsUtils.CHECKBOX
                        )}
                      />
                    </GridLayout.Col>
                  )}
                </GridLayout.Row>
              )}
            </GridLayout>
          )}
        </div>
      )
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/PermissionTab/PermissionBody/Izenda.PermissionScheduling.jsx